import { useEffect, useRef, useState } from 'react';

import { BannerManager } from '../../../services/heartbeat/BannerManager';

export interface BannerContainerReturn {
	/** DOM container element for banner portal */
	bannerContainer: HTMLElement | null;
}

/**
 * Custom hook that manages banner DOM container creation and cleanup.
 * Handles the BannerManager lifecycle and provides the container for portal rendering.
 */
export const useBannerContainer = (isVisible: boolean): BannerContainerReturn => {
	const [bannerContainer, setBannerContainer] = useState<HTMLElement | null>(null);

	// Refs for manager
	const bannerManagerRef = useRef<BannerManager | null>(null);

	// Initialize banner manager
	useEffect(() => {
		if (!bannerManagerRef.current) {
			bannerManagerRef.current = new BannerManager();
		}

		return () => {
			bannerManagerRef.current?.cleanup();
		};
	}, []);

	/**
	 * DOM injection effect: creates banner container as first child of body.
	 * Only creates container when banner should be visible.
	 */
	useEffect(() => {
		if (!isVisible || !bannerManagerRef.current) {
			setBannerContainer(null);
			return;
		}

		const container = bannerManagerRef.current.createBannerContainer();
		setBannerContainer(container);
	}, [isVisible]);

	return {
		bannerContainer,
	};
};
